import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { cn } from "@/lib/utils";

export function SessionUserSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("flex h-8 items-center gap-2 px-1.5", className)}
      data-testid="session-skeleton"
      role="status"
      aria-label="Loading session"
    >
      <Skeleton className="size-7 shrink-0 rounded-full" />
      <Skeleton className="hidden h-2.5 w-14 sm:block" />
    </div>
  );
}

export function SidebarListSkeleton({ rows = 4, testId = "sidebar-skeleton" }: { rows?: number; testId?: string }) {
  return (
    <div className="flex flex-col gap-2 px-3 py-1" data-testid={testId} role="status" aria-label="Loading navigation">
      {Array.from({ length: rows }).map((_, i) => (
        <Skeleton key={i} className="h-8 w-full" />
      ))}
    </div>
  );
}

export function GuideSkeleton() {
  return (
    <div data-testid="guide-skeleton" role="status" aria-label="Loading mission guide" aria-busy="true">
      <Skeleton className="h-3 w-48" />
      <Skeleton className="mt-3 h-6 w-56" />
      <Skeleton className="mt-2 h-3 w-80 max-w-full" />
      <div className="mt-[18px] mb-5 rounded-[4px] border border-border bg-card px-6 py-[22px]">
        <Skeleton className="mb-3 h-2.5 w-16" />
        <Skeleton className="mb-2 h-5 w-40" />
        <Skeleton className="mb-4 h-3 w-full max-w-[560px]" />
        <Skeleton className="mb-4 h-3 w-3/4 max-w-[420px]" />
        <div className="flex gap-2.5">
          <Skeleton className="h-8 w-36 rounded-full" />
          <Skeleton className="h-8 w-32 rounded-full" />
        </div>
      </div>
      <div className="mb-4 overflow-hidden rounded-[4px] border border-border">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="flex items-center gap-3 border-b border-border px-4 py-3 last:border-b-0">
            <Skeleton className="size-3.5 rounded-full" />
            <Skeleton className="h-3.5 w-28" />
            <Skeleton className="ml-auto h-2.5 w-12" />
          </div>
        ))}
      </div>
      <div className="flex gap-4 border-t border-border py-2.5">
        <Skeleton className="h-3 w-40" />
        <Skeleton className="h-3 w-52" />
      </div>
    </div>
  );
}

export function TableSkeleton({
  columns,
  rows = 6,
  testId = "table-skeleton",
}: {
  columns: number;
  rows?: number;
  testId?: string;
}) {
  return (
    <Table aria-busy="true" aria-label="Loading">
      <TableHeader>
        <TableRow>
          {Array.from({ length: columns }).map((_, i) => (
            <TableHead key={i}>
              <Skeleton className="h-3 w-16" />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody data-testid={testId}>
        {Array.from({ length: rows }).map((_, r) => (
          <TableRow key={r} className="hover:bg-transparent">
            {Array.from({ length: columns }).map((_, c) => (
              <TableCell key={c}>
                <Skeleton className={cn("h-3", c === 1 ? "w-40 max-w-full" : "w-20")} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export function ReviewListSkeleton() {
  return (
    <div
      className="overflow-hidden rounded-[4px] border border-border bg-[#0C0C0C]"
      data-testid="review-list-skeleton"
      role="status"
      aria-label="Loading reviews"
      aria-busy="true"
    >
      <div className="grid grid-cols-[minmax(0,1fr)_7.5rem_8.5rem_6.5rem] gap-3 border-b border-border px-3 py-2">
        <Skeleton className="h-2.5 w-16" />
        <Skeleton className="h-2.5 w-12" />
        <Skeleton className="h-2.5 w-16" />
        <Skeleton className="ml-auto h-2.5 w-10" />
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div
          key={i}
          className="grid grid-cols-[minmax(0,1fr)_7.5rem_8.5rem_6.5rem] items-center gap-3 border-l-2 border-transparent px-3 py-2.5"
        >
          <div className="min-w-0 space-y-1.5">
            <Skeleton className="h-3.5 w-56 max-w-full" />
            <Skeleton className="h-3 w-40 max-w-full" />
          </div>
          <Skeleton className="h-5 w-16 rounded-full" />
          <div className="flex gap-2">
            <Skeleton className="size-1.5 rounded-full" />
            <Skeleton className="size-1.5 rounded-full" />
            <Skeleton className="size-1.5 rounded-full" />
          </div>
          <div className="ml-auto space-y-1.5">
            <Skeleton className="ml-auto h-3 w-14" />
            <Skeleton className="ml-auto h-3 w-8" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function ReviewStageSkeleton() {
  return (
    <div
      className="grid min-h-0 min-w-0 flex-1 grid-cols-1 overflow-hidden rounded-[4px] border border-border lg:grid-cols-[220px_minmax(0,1fr)_300px]"
      data-testid="review-stage-skeleton"
      role="status"
      aria-label="Loading design review"
      aria-busy="true"
    >
      <div className="space-y-2 border-r border-border bg-card p-3">
        <Skeleton className="h-2.5 w-20" />
        {Array.from({ length: 6 }).map((_, i) => (
          <Skeleton key={i} className="h-8 w-full" />
        ))}
      </div>
      <div className="space-y-3 bg-muted/20 p-3">
        <Skeleton className="h-8 w-40" />
        <Skeleton className="h-[220px] w-full rounded-md" />
        <Skeleton className="h-24 w-full rounded-md" />
      </div>
      <div className="space-y-2 border-l border-border bg-card p-3">
        <Skeleton className="h-7 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-16 w-full" />
      </div>
    </div>
  );
}

export function HardwareSkeleton() {
  return (
    <div data-testid="hardware-skeleton" role="status" aria-label="Loading hardware" aria-busy="true">
      <div className="mb-3 flex flex-wrap gap-2">
        <Skeleton className="h-8 w-24" />
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-8 w-20" />
      </div>
      <div className="grid min-h-[420px] min-w-0 grid-cols-1 overflow-hidden rounded-lg border border-border md:grid-cols-[280px_1fr]">
        <div className="space-y-2 border-r border-border bg-card p-3">
          <Skeleton className="h-2.5 w-12" />
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-3 w-full" />
          ))}
        </div>
        <div className="p-4">
          <Skeleton className="mb-3 h-8 w-56" />
          <Skeleton className="h-[340px] w-full rounded-md" />
        </div>
      </div>
    </div>
  );
}

export function OrgOverviewSkeleton() {
  return (
    <div className="space-y-4" data-testid="org-skeleton" role="status" aria-label="Loading organization" aria-busy="true">
      <div className="rounded-[4px] border border-border bg-card p-6">
        <Skeleton className="mb-2 h-4 w-32" />
        <Skeleton className="mb-4 h-3 w-full max-w-md" />
        <Skeleton className="mb-3 h-9 w-full max-w-lg" />
        <Skeleton className="h-9 w-full max-w-lg" />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <div className="space-y-2 rounded-[4px] border border-border bg-card p-4">
          <Skeleton className="mb-3 h-4 w-24" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
        <div className="rounded-[4px] border border-border bg-card p-4">
          <Skeleton className="mb-3 h-4 w-24" />
          <TableSkeleton columns={2} rows={4} testId="org-members-skeleton" />
        </div>
      </div>
    </div>
  );
}

export function MilestoneSkeleton() {
  return (
    <div className="mt-4 grid gap-3 md:grid-cols-3" data-testid="milestone-skeleton" role="status" aria-label="Loading milestones" aria-busy="true">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-[4px] border border-border bg-card p-4">
          <Skeleton className="mb-3 h-4 w-32" />
          <Skeleton className="h-3 w-40" />
        </div>
      ))}
    </div>
  );
}

export function SettingsSkeleton() {
  return (
    <div data-testid="settings-skeleton" role="status" aria-label="Loading settings" aria-busy="true">
      <Skeleton className="mb-4 h-24 w-full rounded-[4px]" />
      <Skeleton className="mb-4 h-8 w-72" />
      <TableSkeleton columns={2} rows={4} testId="settings-table-skeleton" />
    </div>
  );
}
